import React, { Component } from "react";
import { FormattedMessage, injectIntl } from "react-intl";

import { faInfoCircle, faToolbox } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { FormGroup, FormLabel, FormSelect, Image } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import BootstrapTable from "react-bootstrap/Table";
import Tooltip from "react-bootstrap/Tooltip";
import { QUESTIONS_TO_ADD } from "../../../../ducks/questionLibrary";
import { realTextLength } from "../../../../utils/QuestionUtils";
import Checkbox from "../../../common/Checkbox";
import LibrarySavedAt from "../../../common/LibrarySavedAt";
import QuestionLibraryModel from "./QuestionLibraryModal";

/**
 *Component which represents a modal for adding question to library
 */
class QuestionLibrarySurveyEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      displayResults: [],
      displayIndicators: [],
      displayQuestions: [],

      selectedActionFieldId: undefined,
      selectedResultIds: [],
      selectedIndicatorIds: [],
      selectedQuestionIds: [],

      disabledResultIds: [],
      disabledIndicatorIds: [],
      disabledQuestionIds: [],

      highlightIndicatorIds: [],
      highlightResultIds: [],
      highlightQuestionIds: [],

      previewQuestion: undefined
    };
  }

  onButtonClick = (name, value) => {
    const { changeProperty } = this.props;
    changeProperty(name, value);
  };

  resetQuestionSelection = (selectedActionFieldId) => {
    this.props.changeProperty(QUESTIONS_TO_ADD, []);

    const selectedQuestionIds = this.props.selectedQuestionIds || [];

    const displayResults = this.props.results.filter((r) =>
      r.actionFields.map((af) => af.id).includes(selectedActionFieldId)
    );
    const displayedResultIds = displayResults.map((result) => result.id);

    const availableDisplayIndicators = this.props.indicators.filter(
      (ind) => ind.result && displayedResultIds.includes(ind.result.id)
    );

    const selectedIndicators = availableDisplayIndicators.filter((indicator) => {
      if (
        this.props.surveyLanguage.id === "de" &&
        indicator.libraryQuestionDe &&
        selectedQuestionIds.includes(indicator.libraryQuestionDe.id)
      ) {
        return true;
      }
      if (
        this.props.surveyLanguage.id === "en" &&
        indicator.libraryQuestionEn &&
        selectedQuestionIds.includes(indicator.libraryQuestionEn.id)
      ) {
        return true;
      }

      return false;
    });
    const selectedIndicatorIds = selectedIndicators.map((indicator) => indicator.identifier);

    const selectedResults = [];
    selectedIndicators.forEach(
      (indicator) =>
        !selectedResults.find((r) => r.id === indicator.result.id) && selectedResults.push(indicator.result)
    );
    const selectedResultIds = selectedResults.map((result) => result.id);

    const displayIndicators = availableDisplayIndicators.filter((indicator) =>
      selectedResultIds.includes(indicator.result.id)
    );

    const displayQuestions = [];
    selectedIndicators.forEach((indicator) => {
      let question;
      if (this.props.surveyLanguage.id === "de" && indicator.libraryQuestionDe) {
        question = indicator.libraryQuestionDe;
      }
      if (this.props.surveyLanguage.id === "en" && indicator.libraryQuestionEn) {
        question = indicator.libraryQuestionEn;
      }

      question && !displayQuestions.find((q) => q.id === question.id) && displayQuestions.push(question);
    });

    this.setState({
      displayResults: displayResults,
      displayIndicators: displayIndicators,
      displayQuestions: displayQuestions,

      selectedActionFieldId: selectedActionFieldId,
      selectedResultIds: selectedResultIds,
      selectedIndicatorIds: selectedIndicatorIds,
      selectedQuestionIds: selectedQuestionIds,

      disabledResultIds: selectedResultIds,
      disabledIndicatorIds: selectedIndicatorIds,
      disabledQuestionIds: selectedQuestionIds
    });
  };

  onAddQuestionClick = () => {
    this.resetQuestionSelection(this.props.actionFields[0].id);

    this.props.getQuestionLibrary();
  };

  onSelectedActionFieldChange = (actionFieldId) => {
    this.resetQuestionSelection(actionFieldId);
  };

  onSelectedResultFieldChange = (resultId, checked) => {
    let selectedResultIds;
    let selectedIndicatorIds = this.state.selectedIndicatorIds;
    if (checked) {
      selectedResultIds = [...this.state.selectedResultIds, resultId];
    } else {
      selectedResultIds = this.state.selectedResultIds.filter((e) => e !== resultId);

      const selectedReferencingIndicatorIds = this.state.displayIndicators
        .filter((ind) => ind.result.id === resultId && selectedIndicatorIds.includes(ind.identifier))
        .map((ind) => ind.identifier);

      if (selectedReferencingIndicatorIds.length > 0) {
        selectedIndicatorIds = selectedIndicatorIds.filter((id) => !selectedReferencingIndicatorIds.includes(id));
      }

      this.state.displayIndicators
        .filter((ind) => ind.result.id === resultId)
        .forEach((ind) =>
          this.changeIndicatorSelectionState(selectedResultIds, selectedIndicatorIds, ind.identifier, false)
        );
    }

    const displayIndicators = this.filterDisplayedIndicators(selectedResultIds);

    this.setState({
      selectedResultIds: selectedResultIds,
      displayIndicators: displayIndicators
    });
  };

  changeIndicatorSelectionState = (selectedResultIds, selectedIndicatorIds, indicatorId, checked) => {
    const { questionLibrary, indicators, addQuestionToTemplate } = this.props;

    if (checked) {
      selectedIndicatorIds = [...selectedIndicatorIds, indicatorId];
    } else {
      selectedIndicatorIds = selectedIndicatorIds.filter((e) => e !== indicatorId);
    }

    const selectedIndicators = indicators.filter((ind) => selectedIndicatorIds.includes(ind.identifier));
    const indicatorsReferencingQuestions = this.filterIndicatorReferencingQuestions(selectedIndicatorIds);
    const indicatorsReferencingQuestionIds = indicatorsReferencingQuestions.map((q) => q.id);

    const indicatorReferencedResults = selectedIndicators.filter((ind) => !!ind.result);
    indicatorReferencedResults.forEach((result) => {
      if (!selectedResultIds.includes(result.id)) {
        selectedResultIds.push(result.id);
      }
    });

    const selectedQuestionIds = this.state.selectedQuestionIds.filter((questionid) => {
      const referenced = indicatorsReferencingQuestionIds.includes(questionid);

      if (!referenced) {
        const question = questionLibrary.find((q) => q.id === questionid);
        question && addQuestionToTemplate(question, false);
      }

      return referenced;
    });

    this.setState({
      selectedResultIds: selectedResultIds,
      selectedIndicatorIds: selectedIndicatorIds,
      selectedQuestionIds: selectedQuestionIds,
      displayQuestions: indicatorsReferencingQuestions
    });
  };

  onSelectedIndicatorChange = (indicatorId, checked) => {
    this.changeIndicatorSelectionState(
      this.state.selectedResultIds,
      this.state.selectedIndicatorIds,
      indicatorId,
      checked
    );
  };

  filterDisplayedIndicators = (selectedResultIds) => {
    const selectedActionFieldId = this.state.selectedActionFieldId;
    const actionFieldsResults = this.props.results.filter((r) =>
      r.actionFields.map((af) => af.id).includes(selectedActionFieldId)
    );
    const actionFieldsResultsIds = actionFieldsResults.map((r) => r.id);

    const indicators = this.props.indicators.filter(
      (ind) => ind.result && actionFieldsResultsIds.includes(ind.result.id) && selectedResultIds.includes(ind.result.id)
    );

    return indicators;
  };

  filterIndicatorReferencingQuestions = (selectedIndicatorIds) => {
    const selectedIndicators = this.props.indicators.filter((i) => selectedIndicatorIds.includes(i.identifier));

    const indicatorsReferencingQuestions = [];

    selectedIndicators.forEach((i) => {
      if (
        this.props.surveyLanguage.id === "de" &&
        i.libraryQuestionDe &&
        !indicatorsReferencingQuestions.find((q) => q.id === i.libraryQuestionDe.id)
      ) {
        indicatorsReferencingQuestions.push(i.libraryQuestionDe);
      }
      if (
        this.props.surveyLanguage.id === "en" &&
        i.libraryQuestionEn &&
        !indicatorsReferencingQuestions.find((q) => q.id === i.libraryQuestionEn.id)
      ) {
        indicatorsReferencingQuestions.push(i.libraryQuestionEn);
      }
    });

    return indicatorsReferencingQuestions;
  };

  flatArray = (array) => {
    return array.reduce((acc, val) => acc.concat(val), []);
  };

  onSelectedQuestionChange = (questionId, checked) => {
    const question = this.state.displayQuestions.find((q) => q.id === questionId);

    this.props.addQuestionToTemplate(question, checked);

    let selectedQuestionIds;
    if (checked) {
      selectedQuestionIds = [...this.state.selectedQuestionIds, questionId];
    } else {
      selectedQuestionIds = this.state.selectedQuestionIds.filter((e) => e !== questionId);
    }

    const newState = { selectedQuestionIds: selectedQuestionIds };

    if (checked) {
      // set only question references indicators as checked

      const selectedIndicatorIds = this.state.selectedIndicatorIds;
      const selectedResultIds = this.state.selectedResultIds;

      this.state.displayIndicators.forEach((ind) => {
        let indicatorId;
        if (
          this.props.surveyLanguage.id === "de" &&
          ind.libraryQuestionDe &&
          selectedQuestionIds.includes(ind.libraryQuestionDe.id) &&
          !selectedIndicatorIds.includes(ind.libraryQuestionDe.id)
        ) {
          indicatorId = ind.identifier;
        }
        if (
          this.props.surveyLanguage.id === "en" &&
          ind.libraryQuestionEn &&
          selectedQuestionIds.includes(ind.libraryQuestionEn.id) &&
          !selectedIndicatorIds.includes(ind.libraryQuestionEn.id)
        ) {
          indicatorId = ind.identifier;
        }

        if (indicatorId) {
          selectedIndicatorIds.push(indicatorId);
          if (!selectedResultIds.includes(ind.result.id)) {
            selectedResultIds.push(ind.result.id);
          }
        }
      });

      newState.selectedIndicatorIds = selectedIndicatorIds;
      newState.selectedResultIds = selectedResultIds;
    }
    this.setState(newState);
  };

  highlightQuestionReferences = (questionId, highlight) => {
    if (highlight) {
      const highlightIndicators = [];
      this.state.displayIndicators.forEach((ind) => {
        if (ind.libraryQuestionDe && ind.libraryQuestionDe.id === questionId) {
          highlightIndicators.push(ind);
        }
        if (ind.libraryQuestionEn && ind.libraryQuestionEn.id === questionId) {
          highlightIndicators.push(ind);
        }
      });

      const highlightResultIds = highlightIndicators.filter((r) => !!r.result).map((r) => r.result.id);

      const highlightIndicatorIds = highlightIndicators.map((i) => i.identifier);

      this.setState({
        highlightIndicatorIds,
        highlightResultIds,
        highlightQuestionIds: [questionId]
      });
    } else {
      this.setState({
        highlightIndicatorIds: [],
        highlightResultIds: [],
        highlightQuestionIds: []
      });
    }
  };

  highlightIndicatorReferences = (indicatorId, highlight) => {
    if (highlight) {
      const indicators = this.state.displayIndicators.filter((i) => i.identifier === indicatorId && i.result);
      const highlightResultIds = indicators.map((i) => i.result.id);
      const highlightQuestionIds = [];
      indicators.forEach((ind) => {
        if (this.props.surveyLanguage.id === "de" && ind.libraryQuestionDe) {
          highlightQuestionIds.push(ind.libraryQuestionDe.id);
        }
        if (this.props.surveyLanguage.id === "en" && ind.libraryQuestionEn) {
          highlightQuestionIds.push(ind.libraryQuestionEn.id);
        }
      });

      this.setState({
        highlightResultIds,
        highlightQuestionIds,
        highlightIndicatorIds: [indicatorId]
      });
    } else {
      this.setState({
        highlightIndicatorIds: [],
        highlightResultIds: [],
        highlightQuestionIds: []
      });
    }
  };

  render() {
    const {
      intl,
      getQuestionLibrary,
      showModalAddQuestionFromLibrary,
      showModalQuestionLibrarySettings,
      surveyLanguageForQuestionLibrary,
      questionName,
      questionId,
      questionIdValid,
      questionResult,
      selectedPage,
      questionActionField,
      questionLibrary,
      previewImageData,
      addQuestionToTemplate,
      questionsToAdd,
      submitAdding,
      actionFields,
      changeProperty,
      addQuestionToLibrary,
      addQuestionToMyLibrary,
      templateLanguages,
      locale,
      questionSync,
      changeQuestionField,
      removeQuestion
    } = this.props;

    const state = this.state;

    return (
      <>
        {questionSync ? (
          <QuestionLibraryModel
            showModalQuestionLibrarySettings={showModalQuestionLibrarySettings}
            showModalAddQuestionFromLibrary={showModalAddQuestionFromLibrary}
            questionName={questionName}
            questionId={questionId}
            questionIdValid={questionIdValid}
            questionActionField={questionActionField}
            questionResult={questionResult}
            previewImageData={previewImageData}
            surveyLanguageForQuestionLibrary={surveyLanguageForQuestionLibrary}
            questionLibrary={questionLibrary}
            templateLanguages={templateLanguages}
            questionsToAdd={questionsToAdd}
            changeProperty={changeProperty}
            addQuestionToLibrary={addQuestionToLibrary}
            getQuestionLibrary={getQuestionLibrary}
            addQuestionToTemplate={addQuestionToTemplate}
            submitAdding={submitAdding}
            changeQuestionField={changeQuestionField}
            selectedPage={selectedPage}
            removeQuestion={removeQuestion}
            questionSync={questionSync}
            target={"settings"}
          />
        ) : (
          <>
            <Button className="text-start" onClick={() => this.onAddQuestionClick()}>
              <FontAwesomeIcon icon={faToolbox} className="me-2" />
              <FormattedMessage id="question.library.toolbox" />
            </Button>
            <QuestionLibraryModel
              showModalQuestionLibrarySettings={showModalQuestionLibrarySettings}
              showModalAddQuestionFromLibrary={showModalAddQuestionFromLibrary}
              questionName={questionName}
              questionId={questionId}
              questionIdValid={questionIdValid}
              questionActionField={questionActionField}
              questionResult={questionResult}
              previewImageData={previewImageData}
              surveyLanguageForQuestionLibrary={surveyLanguageForQuestionLibrary}
              questionLibrary={questionLibrary}
              templateLanguages={templateLanguages}
              questionsToAdd={questionsToAdd}
              changeProperty={changeProperty}
              addQuestionToLibrary={addQuestionToMyLibrary}
              getQuestionLibrary={getQuestionLibrary}
              addQuestionToTemplate={addQuestionToTemplate}
              submitAdding={submitAdding}
              changeQuestionField={changeQuestionField}
              selectedPage={selectedPage}
              removeQuestion={removeQuestion}
              target={"settings"}
            />
            {this.state.previewQuestion && (
              <Modal show size="xl">
                <Modal.Header>
                  <Modal.Title>
                    <FormattedMessage id="question.preview" />
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Image fluid src={this.state.previewQuestion.previewImageData} />
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => this.setState({ previewQuestion: undefined })}>
                    <FormattedMessage id="app.common.Close" />
                  </Button>
                </Modal.Footer>
              </Modal>
            )}
            <Modal show={showModalAddQuestionFromLibrary} size="xl">
              <Modal.Header>
                <Modal.Title>
                  <FormattedMessage id="survey.editor.add.question.library.title" />
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <FormGroup  className="mb-3">
                  <FormLabel>
                    <FormattedMessage id="indicator.management.action.field" />
                  </FormLabel>
                  <FormSelect
                    onChange={(event) => this.onSelectedActionFieldChange(event.target.value)}
                    value={state.selectedActionFieldId || ""}
                  >
                    <option value="">{intl.formatMessage({ id: "indicator.management.action.field" })}</option>
                    {actionFields &&
                      actionFields.map((af) => (
                        <option key={af.id} value={af.id}>
                          {af.name[locale]}
                        </option>
                      ))}
                  </FormSelect>
                </FormGroup>
                <BootstrapTable className="question-library">
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: "indicator.management.results" })}</th>
                      <th>{intl.formatMessage({ id: "indicator.management.indicators" })}</th>
                      <th>{intl.formatMessage({ id: "indicator.management.questions" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {state.displayResults &&
                          state.displayResults.map((result) => (
                            <FormGroup className="mb-3" key={result.id}>
                              <Checkbox
                                disabled={state.disabledResultIds.includes(result.id)}
                                value={result.id}
                                checked={state.selectedResultIds.includes(result.id)}
                                name={"actionField"}
                                onChange={(e) => this.onSelectedResultFieldChange(e.target.value, e.target.checked)}
                              >
                                <span
                                  style={
                                    state.highlightResultIds.includes(result.id) ? { backgroundColor: "#e4eef7" } : {}
                                  }
                                >
                                  {result.name[locale]}&nbsp;
                                </span>
                                {result.description &&
                                  result.description[locale] &&
                                  realTextLength(result.description[locale]) > 0 && (
                                    <OverlayTrigger
                                      placement={"right"}
                                      overlay={
                                        <Tooltip id={`tooltip_${result.id}`}>
                                          <div dangerouslySetInnerHTML={{ __html: result.description[locale] }} />
                                        </Tooltip>
                                      }
                                    >
                                      <FontAwesomeIcon icon={faInfoCircle} />
                                    </OverlayTrigger>
                                  )}
                              </Checkbox>
                            </FormGroup>
                          ))}
                      </td>
                      <td>
                        {state.displayIndicators &&
                          state.displayIndicators.map((indicator) => (
                            <FormGroup className="mb-3" key={indicator.identifier}>
                              <Checkbox
                                disabled={state.disabledIndicatorIds.includes(indicator.identifier)}
                                value={indicator.identifier}
                                checked={state.selectedIndicatorIds.includes(indicator.identifier)}
                                name="indicator"
                                onChange={(e) => this.onSelectedIndicatorChange(e.target.value, e.target.checked)}
                              >
                                <span
                                  style={
                                    state.highlightIndicatorIds.includes(indicator.identifier)
                                      ? { backgroundColor: "#e4eef7" }
                                      : {}
                                  }
                                  onMouseOver={() => this.highlightIndicatorReferences(indicator.identifier, true)}
                                  onMouseOut={() => this.highlightIndicatorReferences(indicator.identifier, false)}
                                >
                                  {indicator.name[locale]}&nbsp;
                                </span>
                                {indicator.longName &&
                                  indicator.longName[locale] &&
                                  realTextLength(indicator.longName[locale]) > 0 && (
                                    <OverlayTrigger
                                      placement={"right"}
                                      overlay={
                                        <Tooltip id={`tooltip_${indicator.id}`}>
                                          <div dangerouslySetInnerHTML={{ __html: indicator.longName[locale] }} />
                                        </Tooltip>
                                      }
                                    >
                                      <FontAwesomeIcon icon={faInfoCircle} />
                                    </OverlayTrigger>
                                  )}
                              </Checkbox>
                            </FormGroup>
                          ))}
                      </td>
                      <td>
                        {state.displayQuestions &&
                          state.displayQuestions.map((question) => (
                            <FormGroup className="mb-3" key={question.id}>
                              <Checkbox
                                value={question.id}
                                type="checkbox"
                                disabled={state.disabledQuestionIds.includes(question.id)}
                                checked={state.selectedQuestionIds.includes(question.id)}
                                onChange={(e) => this.onSelectedQuestionChange(e.target.value, e.target.checked)}
                              >
                                <Button
                                  className="text-start p-0"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.setState({ previewQuestion: question });
                                  }}
                                  variant="link"
                                  disabled={!question.previewImageData}
                                  title={intl.formatMessage({ id: "indicator.management.question.preview" })}
                                  style={
                                    state.highlightQuestionIds.includes(question.id)
                                      ? { backgroundColor: "#e4eef7" }
                                      : {}
                                  }
                                  onMouseOver={() => this.highlightQuestionReferences(question.id, true)}
                                  onMouseOut={() => this.highlightQuestionReferences(question.id, false)}
                                >
                                  <div dangerouslySetInnerHTML={{ __html: question.name }} />
                                </Button>
                              </Checkbox>
                              <small className="text-muted d-block">
                                <FormattedMessage id="library.savedAt" />: <LibrarySavedAt value={question.updated} />
                              </small>
                            </FormGroup>
                          ))}
                      </td>
                    </tr>
                  </tbody>
                </BootstrapTable>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => this.onButtonClick("showModalAddQuestionFromLibrary", false)}
                >
                  <FormattedMessage id="app.common.Close" />
                </Button>
                <Button disabled={!questionsToAdd?.length} onClick={() => submitAdding()}>
                  <FormattedMessage id="survey.editor.add.question" />
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </>
    );
  }
}

QuestionLibrarySurveyEditor.propTypes = {
  showModalQuestionLibrarySettings: PropTypes.bool,
  showModalAddQuestionFromLibrary: PropTypes.bool,
  questionName: PropTypes.string,
  questionId: PropTypes.string,
  questionIdValid: PropTypes.bool,
  surveyLanguageForQuestionLibrary: PropTypes.object,
  questionLibrary: PropTypes.array,
  templateLanguages: PropTypes.array,
  questionsToAdd: PropTypes.array,

  actionFields: PropTypes.arrayOf(PropTypes.object),
  results: PropTypes.arrayOf(PropTypes.object),
  indicators: PropTypes.arrayOf(PropTypes.object),
  questions: PropTypes.arrayOf(PropTypes.object),

  selectedActionField: PropTypes.object,
  selectedResult: PropTypes.object,

  /**
   * An array of pre selected library question IDs which cannot be unchecked.
   */
  selectedQuestionIds: PropTypes.arrayOf(PropTypes.string),

  /**
   * Current browser locale to display translated objects accordingly.
   */
  locale: PropTypes.string.isRequired,

  changeProperty: PropTypes.func,
  addQuestionToLibrary: PropTypes.func,
  getQuestionLibrary: PropTypes.func,
  addQuestionToTemplate: PropTypes.func,
  submitAdding: PropTypes.func,
  questionSync: PropTypes.bool,
  changeQuestionField: PropTypes.func,
  removeQuestion: PropTypes.func
};

export default injectIntl(QuestionLibrarySurveyEditor);
